/**
 * Auth API client (PRD #63, #64; tech-spec §5.1).
 *
 * Thin wrapper over `fetch` for the four auth endpoints. The session
 * cookie is httpOnly so JS never touches it directly — every call uses
 * `credentials: 'include'` so the browser attaches the cookie.
 *
 * No external HTTP client; standard `fetch` is enough for these four
 * round trips and keeps the bundle slim.
 */

export type AuthUser = { email: string }

export type RequestLinkResult = { ok: true } | { ok: false; status: number; message: string }
export type VerifyResult = { ok: true; user: AuthUser } | { ok: false; status: number; message: string }
export type MeResult = { ok: true; user: AuthUser } | { ok: false; status: number }

const API_BASE = '/api/v1'

export async function requestMagicLink(email: string, locale: string): Promise<RequestLinkResult> {
  try {
    const response = await fetch(`${API_BASE}/auth/request-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, locale }),
    })
    if (response.status === 204) return { ok: true }
    return { ok: false, status: response.status, message: await safeMessage(response) }
  } catch (cause) {
    return {
      ok: false,
      status: 0,
      message: cause instanceof Error ? cause.message : String(cause),
    }
  }
}

export async function verifyMagicLink(token: string): Promise<VerifyResult> {
  try {
    const response = await fetch(
      `${API_BASE}/auth/verify?token=${encodeURIComponent(token)}`,
      { credentials: 'include' },
    )
    if (response.ok) {
      const body = (await response.json()) as { user: AuthUser }
      return { ok: true, user: body.user }
    }
    return { ok: false, status: response.status, message: await safeMessage(response) }
  } catch (cause) {
    return {
      ok: false,
      status: 0,
      message: cause instanceof Error ? cause.message : String(cause),
    }
  }
}

export async function fetchMe(): Promise<MeResult> {
  try {
    const response = await fetch(`${API_BASE}/me`, { credentials: 'include' })
    if (response.status === 401) return { ok: false, status: 401 }
    if (!response.ok) return { ok: false, status: response.status }
    const body = (await response.json()) as { user: AuthUser }
    return { ok: true, user: body.user }
  } catch {
    return { ok: false, status: 0 }
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch {
    // Logout is fire-and-forget. Even if the request fails, we drop
    // the local auth state — the user wanted to sign out.
  }
}

async function safeMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } }
    return body.error?.message ?? `HTTP ${response.status}`
  } catch {
    return `HTTP ${response.status}`
  }
}
